// Angular
import { Router, NavigationEnd } from '@angular/router';
import { Component, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';

// NGRX & RXJS
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, of, timer } from 'rxjs';
import { catchError, filter, map, switchAll, take } from 'rxjs/operators';

// Miscellaneous
import * as olci from '../../../actions/olci';
import * as fromRoot from '../../../reducers';
import { constants } from '../../../constants';
import * as tracking from '../../../actions/tracking';
import { PromptComponent } from '../prompt/prompt.component';
import { GuestService } from '../../core/services/guest.service';
import { GatingServices } from '../../core/services/gating.services';
import { BookingService } from '../../core/services/booking.service';
import { MicroServicesService } from '../../core/services/micro-services.service';
import { ResourceBundleService } from '../../core/services/resource-bundle.service';

@Component({
    selector: 'app-session-timeout',
    templateUrl: './session-timeout.component.html',
    styleUrls: ['./session-timeout.component.scss'],
    standalone: false
})
export class SessionTimeoutComponent extends PromptComponent implements AfterViewInit, OnDestroy {
	public routerPath$: Observable<any>;
	public bundleSubscription: Subscription;
	public resourceBundle: any;
	public sessionTimestamp$: Observable<any>;
	public sessionWarningModal$: Observable<boolean> | any;
	public showExtendSessionModal = true;
	public showEndedSessionModal = true;
	public timeoutExtendModal = false;
	public timeoutEndedModal = false;
	public countdownTimerObj: any;
	public minutes = 2;
	public minutesText: string;
	public seconds = 0;
	public secondsTwoDigits: string;
	public guestAccountDashboard: string;
	public timeoutSubscription: Subscription;

	constructor(
		protected ref: ChangeDetectorRef,
		private readonly router: Router,
		private readonly gatingServices: GatingServices,
		private resourceService: ResourceBundleService,
		private readonly store: Store<fromRoot.AppState>,
		private readonly bookingService: BookingService,
		private readonly guestService: GuestService,
		private microServices: MicroServicesService
	) {
		super();
		this.routerPath$ = this.store.pipe(select(fromRoot.getRouterPath));
		this.sessionTimestamp$ = this.store.pipe(select(fromRoot.getSessionTimestamp));
		this.sessionWarningModal$ = this.store.pipe(select(fromRoot.getSessionWarningModal));
		this.bundleSubscription = this.resourceService.bundle.subscribe(bundle => (this.resourceBundle = bundle));
		this.guestAccountDashboard = this.gatingServices.getGADashboardURL();
	}

	public override ngAfterViewInit() {
		this.router.events
			.pipe(filter((e: any) => e instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
				if (event.url && event.url === '/resretrieval') {
					this.showExtendSessionModal = false;
				} else if (event.url && event.url === '/resretrieval?tenant=royal') {
					this.showExtendSessionModal = false;
				} else if (event.url && event.url === '/resretrieval?tenant=celebrity') {
					this.showExtendSessionModal = false;
				} else if (event.url && event.url === '/resretrieval/2') {
					this.showExtendSessionModal = false;
				} else if (event.url && event.url === '/beforeyoucruise') {
					this.showExtendSessionModal = false;
				} else if (event.url && event.url === '/resselection') {
					this.showExtendSessionModal = false;
				} else {
					this.showExtendSessionModal = true;
				}
			});

		this.timeoutSubscription = this.sessionTimestamp$
			// .map((x) => Observable.timer(1000 * 60 * 2.5)) // 2.5 minutes for dev or QA purposes
			.pipe(
				map((x: any) => timer(1000 * 60 * 12.5)), // (1000 * 60 * 17.5) = 17.5 minutes
				switchAll()) // creates a new instance of the timer when sessionTimestamp$ changes. The timer restarts again.
			.subscribe((x: any) => {
				// this is what happens when the timer ends.
				if (this.showExtendSessionModal) {
					this.showEndedSessionModal = true;
					this.timeoutExtendModal = true;
					const inert = document.createAttribute('inert');
					document.getElementById('main')?.setAttributeNode(inert);
					this.store.dispatch(new olci.SessionWarningModalAction());
					this.store.dispatch(
						new tracking.TrackPageInteractionEventAction(
							'modal: session warning: your session is about to expire. if no action is taken, you will be logged out in 2 minutes.'
						)
					);
					this.countdownTimerObj = timer(0, 1000)
						.pipe(take(121))
						.subscribe((y: any) => {
							this.minutes = 120 - y - 60 > 0 ? 1 : 0;
							this.seconds = 120 - y - 60 > 0 ? 120 - y - 61 : 120 - y - 1;
							this.minutesText =
								this.minutes === 1
									? this.resourceBundle['time.out.message.minute.inactivity']
									: this.resourceBundle['time.out.message.15.minutes.inactivity2'];
							this.secondsTwoDigits = this.seconds.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
							setTimeout(() => this.ref.detectChanges(), 0);
						});
				}
			});

		this.sessionWarningModal$
			.pipe(
				filter(x => x === true), // only observe a value of true
				map(x => timer(1000 * 60 * 2))) // (1000 * 60 * 2) = 2 minutes
			.pipe(switchAll())
			.subscribe((x: any) => {
				if (this.showEndedSessionModal) {
					// this is the second modal.
					this.timeoutExtendModal = false;
					this.endSessionCondition();
					this.timeoutEndedModal = true;
					setTimeout(() => this.ref.detectChanges(), 0);
					this.store.dispatch(new olci.ClearStateOnlyAction());
					this.store.dispatch(new tracking.TrackPageInteractionEventAction('modal: session ended: oops, your session has timed out.'));
				}
			});
	}

	ngOnDestroy() {
		this.bundleSubscription.unsubscribe();
		if (this.timeoutSubscription) {
			this.timeoutSubscription.unsubscribe();
		}
	}

	// Every api call updates the sessionTimestamp in the guards.
	// When the sessionTimestamp has not changed in 15 minutes,
	// the warning modal pops up and sets the sessionWarningModal to true.
	// This starts a second timer on the sessionWarningModal.
	// Clicking on the modal button to Extend Session updates the sessionTimestamp,
	// and sets the sessionWarningModal to false.
	// some local variables were used to show or not show the modals.
	// If the Extend Session button is not clicked, the timer on the sessionWarningModal reaches 2 minutes,
	// the SessionWarningModal is closed, the End Session Modal is popped up, and the endSessionAction is called.

	timeoutExtendModalHandler() {
		if (!this.timeoutExtendModal) {
			return;
		}

		this.timeoutExtendModal = false;
		this.showEndedSessionModal = false;
		this.store.dispatch(
			new tracking.TrackPageCustomInteractionEventAction({
				event: 'staySession',
				interaction: 'modal: session warning: extend time button clicked'
			})
		);
		document.getElementById('main')?.removeAttribute('inert');
	}

	logoutFromExtendModalHandler() {
		this.endSessionCondition();
		(<any>window).location.href = this.guestAccountDashboard;
		this.store.dispatch(
			new tracking.TrackPageCustomInteractionEventAction({
				event: 'leaveSession',
				interaction: 'modal: session warning: logout button clicked'
			})
		);
		this.timeoutExtendModal = false;
		this.showEndedSessionModal = false;
		document.getElementById('main')?.removeAttribute('inert');
		window.localStorage.removeItem(constants.LOCAL_STORAGE_KEYS.MOBILE_AD_MODAL);
	}

	timeoutEndedModalHandler() {
		(<any>window).location.href = this.guestAccountDashboard;
		this.showExtendSessionModal = false;
		this.showEndedSessionModal = false;
		this.timeoutEndedModal = false;
		this.store.dispatch(new olci.ClearStateOnlyAction());
		this.store.dispatch(new tracking.TrackPageInteractionEventAction('modal: session ended: digital checkin button clicked'));
		document.getElementById('main')?.removeAttribute('inert');
		window.localStorage.removeItem(constants.LOCAL_STORAGE_KEYS.MOBILE_AD_MODAL);
	}

	public endSessionCondition() {
		if (!this.bookingService.sessionIsEnded && this.bookingService.checkinToken) {
			this.sessionEndMicroservice()
				.pipe(take(1))
				.subscribe(ok => {
					this.store.dispatch(new olci.ClearStateOnlyAction());
				});
		}
	}

	public sessionEndMicroservice(): Observable<any> {
		const payload = {
			checkinToken: this.bookingService.checkinToken,
			guests: this.guestService.getAllGuestsBookingCombination()
		};
		return this.microServices
			.guestCheckinEnd(payload)
			.pipe(map(resp => {
				return resp;
			}),
			catchError(error => {
				return of(error);
			}));
	}
}
